/**
 * On-device measure votes for a signed-in user's DRAFT ballots — the measures
 * counterpart to ``draftSelectionsStore`` (candidates). A draft's Yes/No picks
 * stay on the device (localStorage) and reach Cosmos only when the ballot is
 * saved.
 *
 * Keyed per ballot id so each draft (including per-election tabs at one
 * address) keeps its own votes. Listed in PERSISTENT_KEYS so it survives the
 * beforeunload refresh wipe, and is cleared on explicit sign-out with the rest
 * of VoteMate storage.
 */
const STORAGE_KEY = 'votemate_draft_measure_selections';

type DraftMeasureMap = Record<string, Record<string, string>>;

const read = (): DraftMeasureMap => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return {};
    return parsed as DraftMeasureMap;
  } catch {
    return {};
  }
};

const write = (map: DraftMeasureMap): void => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(map));
  } catch {
    // storage full / disabled (private mode, quota) — best-effort.
  }
};

/** On-device votes for a ballot, or ``null`` when this device has no working
 * copy (so callers can fall back to the last-saved Cosmos selections). An
 * empty object means the user cleared every vote — distinct from null. */
export const getDraftMeasureSelections = (ballotId: string): Record<string, string> | null => {
  const map = read();
  if (!(ballotId in map)) return null;
  const votes = map[ballotId];
  return votes && typeof votes === 'object' ? votes : {};
};

export const setDraftMeasureSelections = (
  ballotId: string,
  votes: Record<string, string>
): void => {
  const map = read();
  map[ballotId] = { ...votes };
  write(map);
};

export const clearDraftMeasureSelections = (ballotId: string): void => {
  const map = read();
  if (ballotId in map) {
    delete map[ballotId];
    write(map);
  }
};

/** Drop cache entries for ballots that no longer exist (e.g. after deletion). */
export const pruneDraftMeasureSelections = (validIds: string[]): void => {
  const map = read();
  const valid = new Set(validIds);
  let changed = false;
  for (const id of Object.keys(map)) {
    if (!valid.has(id)) {
      delete map[id];
      changed = true;
    }
  }
  if (changed) write(map);
};
